import { Component, OnInit, Inject } from '@angular/core';
import { SelectType } from '../../cpq-select/cpq-select.component';
import { ILookup } from '../../common/interfaces/ILookup.interface';
import { TableListTagService } from './table-list-tag-builder.service';
import { TableListBuilderModel, Condition, ConditionColumnType, ColumnType, ReturnType, ConditionOperationType, OrderType } from './table-list-tag-builder.model';
import { SyntaxCheckResponse, FormulaBuilderConfig } from '../cpq-formula-builder.model';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PreviewDataTableComponent } from './preview-data-table/preview-data-table.component';
import { MessagesService } from '../../common/services/messages.service';
import { TranslationService } from '../../common/services/translation.service';
import { any } from '../../common/functions/js-linq';

@Component({
    selector: 'table-list-tag-builder',
    templateUrl: './table-list-tag-builder.component.html',
    providers: [TableListTagService],
    styleUrls: ['./table-list-tag-builder.component.scss'],
    standalone: false
})
export class TableListTagBuilderComponent implements OnInit {

  public model: TableListBuilderModel;
  public formula: string;
  public formulaTemplate: string;
  public placeholder = '@tableListPlaceholder@';
  public tableNames: Array<ILookup>;
  public columnNames: Array<ILookup>;
  public returnTypes: Array<ILookup<number, string>>;
  public columnTypes: Array<ILookup<number, string>>;
  public matchingOperations: Array<ILookup<number, string>>;
  public sortTypes: Array<ILookup<number, string>>;
  public syntaxCheckResponse: SyntaxCheckResponse;
  public headerTranslationKey: string;

  public SelectType = SelectType;

  private hasMatchOperation: boolean;

  constructor(private tableListTagService: TableListTagService,
    public messagesService: MessagesService,
    public translationService: TranslationService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<TableListTagBuilderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FormulaBuilderConfig) { 
      this.dialogRef.disableClose = true;
    }

  ngOnInit() {
    this.model = new TableListBuilderModel();
    this.formulaTemplate = this.data.isTableTag ? `<* TABLE ( ${this.placeholder} ) *>` : `<* LIST ( ${this.placeholder} ) *>`;
    this.headerTranslationKey = this.data.isTableTag ? 'FormulaBuilder_TableSyntaxBuilderDialogHeader' : 'FormulaBuilder_ListSyntaxBuilderDialogHeader';

    this.tableListTagService.getTableNames().subscribe(tableNames => {
      if (!any(tableNames)) {
        this.messagesService.showAlertDialog({title: '', content: 'TableBuilderAspx_NoCustomTablesMsg'});
        this.dialogRef.close('');
        return;
      }
      
      this.tableNames = tableNames;
    });

    this.matchingOperations = this.tableListTagService.getMatchingOperations()
    this.returnTypes = this.tableListTagService.getReturnTypes();
    this.columnTypes = this.tableListTagService.getColumnTypes();
    this.sortTypes = this.tableListTagService.getSortTypes();
  }

  public tableNameChanged(event: number) {
    this.modelChangeEventInternal(this.model, 'tableName', event, this.tableNames);

    if(event) {
      this.tableListTagService.getColumnNames(this.model.tableName.text).subscribe((columnNames: Array<ILookup>) => {
        this.columnNames = columnNames;

        // Select first column
        this.modelChangeEventInternal(this.model, 'columnName', 1, this.columnNames);

        this.model.conditions = new Array<Condition>();

        this.addNewCondition();
      })
    } 
  }

  public columnNameChanged(event: number) {
    this.modelChangeEventInternal(this.model, 'columnName', event, this.columnNames);
  }

  public returnTypeChanged(event: number) {
    this.modelChangeEventInternal(this.model, 'returnType', event, this.returnTypes);
  }

  public columnTypeChanged(event: number) {
    this.modelChangeEventInternal(this.model, 'columnType', event, this.columnTypes);
  }

  public sortTypeChanged(event: number) {
    this.modelChangeEventInternal(this.model, 'sortType', event, this.sortTypes);
  }

  public conditionColumnNameChanged(event: number, condition: Condition) {
    this.modelChangeEventInternal(condition, 'columnName', event, this.columnNames);
  }

  public conditionColumnTypeChanged(event: number, condition: Condition) {
    this.modelChangeEventInternal(condition, 'columnType', event, this.columnTypes);
    switch (event) {
      case ConditionColumnType.Text:
        condition.operations = this.tableListTagService.getTextOperations();
        this.modelChangeEventInternal(condition, 'operation', 1, condition.operations);
        break;
      case ConditionColumnType.Number:
      case ConditionColumnType.Date:
        condition.operations = this.tableListTagService.getNumericAndDateOperations(this.hasMatchOperation);
        this.modelChangeEventInternal(condition, 'operation', 1, condition.operations);
        break;
    }

  }

  public conditionOperationChanged(event: number, condition: Condition) {
    this.modelChangeEventInternal(condition, 'operation', event, condition.operations);

    this.reloadOperations(condition);
  }


  public addNewCondition() {
    const condition = new Condition();
    this.model.conditions.push(condition);
  }

  public removeCondition(conditionIdx: number) {
    this.model.conditions.splice(conditionIdx, 1);
  }

  public testFormula() {
    this.tableListTagService.testFormula(this.formula, this.data.isTableTag).subscribe((response: SyntaxCheckResponse) => {
      this.syntaxCheckResponse = response;
    })
  }

  public buildFormula() {
    // Validation of fields
    if (!this.model.tableName.id) {
      this.messagesService.showAlertDialog({title: '', content: 'FormulaBuilder.AlertMsg.Table'});
      return;
    } else if (!this.model.returnType.id) {
      this.messagesService.showAlertDialog({title: '', content: 'FormulaBuilder.AlertMsg.ReturnType'});
      return;
    } else if (!this.model.columnName.id) {
      this.messagesService.showAlertDialog({title: '', content: 'FormulaBuilder.AlertMsg.Column'});
      return;
    } else if (!this.model.columnType.id) {
      this.messagesService.showAlertDialog({title: '', content: 'FormulaBuilder.AlertMsg.ColumnType'});
      return;
    } else {
      this.model.conditions.forEach(condition => {
        if (!condition.columnName || !condition.columnName.id) {
          this.messagesService.showAlertDialog({title: '', content: 'FormulaBuilder.AlertMsg.TableConditionColumn'});
          return;
        } else if (!condition.columnType || !condition.columnType.id) {
          this.messagesService.showAlertDialog({title: '', content: 'FormulaBuilder.AlertMsg.TableConditionColumnType'});
          return;
        } else if (!condition.operation || !condition.operation.id) {
          this.messagesService.showAlertDialog({title: '', content: 'FormulaBuilder.AlertMsg.TableConditionOperator'});
          return;
        }
      })
    }

    let sqlQuery = 'SELECT';
    this.formula = this.formulaTemplate;

    if (this.hasMatchOperation) {
      sqlQuery = `${sqlQuery} TOP 1`;
      sqlQuery = this.appendExactReturnType(sqlQuery);
    } else {
      sqlQuery = this.appendReturnType(sqlQuery);
    }

    sqlQuery = `${sqlQuery} FROM ${this.model.tableName.text} WHERE `;

    // Appending conditions
    let counter = this.model.conditions.length;
    this.model.conditions.forEach(condition => {
      sqlQuery = this.appendConditionColumn(sqlQuery, condition);
      sqlQuery = this.appendConditionOperation(sqlQuery, condition);

      if (--counter > 0) {
        sqlQuery = `${sqlQuery} AND `;
      }
    });

    sqlQuery = this.appendOrderBy(sqlQuery);

    this.formula = this.formulaTemplate.replace(this.placeholder, sqlQuery);

  }

  public pushTagToFormula() {
    this.tableListTagService.testFormula(this.formula, this.data.isTableTag).subscribe(response => {
      if (response.status.toLowerCase() === 'error') {
        this.messagesService.showAlertDialog({title: '', content: response.message});
      } else {
        this.dialogRef.close(this.formula);
      }
    })
  }

  public previewDataTable() {
    if (this.model.tableName.id) {
      this.dialog.open(PreviewDataTableComponent, {
        data: {
          tableName: this.model.tableName.text,
        }
      });
    } else {
        this.messagesService.showAlertDialog({title: '', content: 'FormulaBuilder.AlertMsg.PreviewTable'});
        return;
    }
  }

  public cancel() {
    this.dialogRef.close('');
  }

  private appendReturnType(sqlQuery: string) {
    switch (this.model.returnType.id) {
      case ReturnType.Exact:
        sqlQuery = this.appendExactReturnType(sqlQuery);
        break;
      case ReturnType.Min:
        sqlQuery = `${sqlQuery} MIN ( CAST (${this.model.columnName.text} AS float ))`;
        break;
      case ReturnType.Max:
        sqlQuery = `${sqlQuery} MAX ( CAST (${this.model.columnName.text} AS float ))`;
        break;
      case ReturnType.Count:
        sqlQuery = `${sqlQuery} COUNT ( ${this.model.columnName.text} )`;
        break;
      case ReturnType.Average:
        sqlQuery = `${sqlQuery} AVG ( CAST (${this.model.columnName.text} AS float ))`;
        break;
      case ReturnType.Sum:
        sqlQuery = `${sqlQuery} SUM ( CAST (${this.model.columnName.text} AS float ))`;
        break;
    }
    return sqlQuery;
  }

  private appendExactReturnType(sqlQuery: string) {
    switch (this.model.columnType.id) {
      case ColumnType.Text:
        sqlQuery = `${sqlQuery} ${this.model.columnName.text}`;
        break;
      case ColumnType.Number:
        sqlQuery = `${sqlQuery} CAST (${this.model.columnName.text} AS FLOAT ) AS ${this.model.columnName.text}`;
        break;
      case ColumnType.Date:
        sqlQuery = `${sqlQuery} CAST (${this.model.columnName.text} AS DATETIME ) AS ${this.model.columnName.text}`;
        break;
    }
    return sqlQuery;
  }

  private appendConditionColumn(sqlQuery: string, condition: Condition) {
    switch (condition.columnType.id) {
      case ConditionColumnType.Text:
        sqlQuery = `${sqlQuery} ${condition.columnName.text}`;
        break;
      case ConditionColumnType.Number:
        sqlQuery = `${sqlQuery} CAST (${condition.columnName.text} AS float)`;
        break;
      case ConditionColumnType.Date:
        sqlQuery = `${sqlQuery} CAST (${condition.columnName.text} AS datetime)`;
        break;
    }
    return sqlQuery;
  }

  private appendConditionOperation(sqlQuery: string, condition: Condition) {
    switch (condition.operation.id) {
      case ConditionOperationType.Equal:
        sqlQuery = `${sqlQuery} = '${condition.value}'`;
        break;
      case ConditionOperationType.GreaterThan:
        sqlQuery = `${sqlQuery} > '${condition.value}'`;
        break;
      case ConditionOperationType.GreaterThanOrEqual:
        sqlQuery = `${sqlQuery} >= '${condition.value}'`;
        break;
      case ConditionOperationType.LessThan:
        sqlQuery = `${sqlQuery} < '${condition.value}'`;
        break;
      case ConditionOperationType.LessThanOrEqual:
        sqlQuery = `${sqlQuery} <= '${condition.value}'`;
        break;
      case ConditionOperationType.BeginsWith:
        sqlQuery = `${sqlQuery} LIKE '${condition.value}%'`;
        break;
      case ConditionOperationType.EndsWith:
        sqlQuery = `${sqlQuery} LIKE '%${condition.value}'`;
        break;
      case ConditionOperationType.Matches:
        sqlQuery = `${sqlQuery} LIKE '%${condition.value}%'`;
        break;
      case ConditionOperationType.MatchLower: 
        sqlQuery = `${sqlQuery} < '${condition.value}'`;
        break;
      case ConditionOperationType.MatchLowerInclusive: 
        sqlQuery = `${sqlQuery} <= '${condition.value}'`;
        break;
      case ConditionOperationType.MatchUpper: 
        sqlQuery = `${sqlQuery} > '${condition.value}'`;
        break;
      case ConditionOperationType.MatchUpperInclusive: 
        sqlQuery = `${sqlQuery} >= '${condition.value}'`;
        break;
    }
    return sqlQuery;
  }

  private appendOrderBy(sqlQuery: string) {
    let isColumnNameInMatchConditionOperation = false;

    if (this.hasMatchOperation) {
      sqlQuery = `${sqlQuery} ORDER BY `;
      this.model.conditions.forEach(condition => {
        if (this.matchingOperations.find(operation => operation.id === condition.operation.id)) {
          if (condition.columnType.id === ColumnType.Text || condition.columnType.id === ColumnType.Number) {
            sqlQuery = `${sqlQuery} CAST (${condition.columnName.text} AS float)`;
          } else {
            sqlQuery = `${sqlQuery} CAST (${condition.columnName.text} AS DateTime)`;
          }

          if (condition.operation.id === ConditionOperationType.MatchLower || condition.operation.id === ConditionOperationType.MatchLowerInclusive) {
            sqlQuery = `${sqlQuery} DESC`;
          } else if (condition.operation.id === ConditionOperationType.MatchUpper || condition.operation.id === ConditionOperationType.MatchUpperInclusive) {
            sqlQuery = `${sqlQuery} ASC`;
          }

          if (this.model.columnName.id === condition.columnName.id) {
            isColumnNameInMatchConditionOperation = true;
          }
        }
      });
    }

    switch (this.model.sortType.id) {
      case OrderType.Asc:
        sqlQuery = `${sqlQuery} ORDER BY ${this.model.columnName.text} ASC`;
        break;
      case OrderType.Desc:
        sqlQuery = `${sqlQuery} ORDER BY ${this.model.columnName.text} DESC`;
        break;
    }

    return sqlQuery;
  }

  private modelChangeEventInternal(model: any, modelProperty: string, id: any, array: Array<any>) {
    if (id) {
      const element = array.find(el => el.id.toString() === id.toString());

      if (element) {
        model[modelProperty].text = element.text;
        model[modelProperty].id = element.id;
      }
    } else {
      model[modelProperty] = null;
      this.formula = '';
    }
  }

  private checkMatchOperation() {
    this.hasMatchOperation = false;

    this.model.conditions.forEach(cond => {
      switch(cond.operation.id){
        case ConditionOperationType.MatchLower:
        case ConditionOperationType.MatchLowerInclusive:
        case ConditionOperationType.MatchUpper:
        case ConditionOperationType.MatchUpperInclusive:
          this.hasMatchOperation = true;
      }
    })
  }

  private reloadOperations(condition: Condition) {
    this.checkMatchOperation();

    this.model.conditions.filter(cond => cond !== condition).forEach(cond => {
      cond.operations = cond.columnType.id === ConditionColumnType.Text ? this.tableListTagService.getTextOperations() : this.tableListTagService.getNumericAndDateOperations(this.hasMatchOperation);
    })
  }
}
